import { isAfter, isBefore, UTCDate } from '@/utils/dates';
import { Row } from '@tanstack/react-table';

/**
 * Sort function for TanStack Table that handles date columns with support for "Unknown" values.
 *
 * Sorting behavior:
 * - "Unknown" dates are always placed at the **top** of the sorted list (come first)
 * - Valid dates are sorted chronologically (earliest to latest when ascending)
 * - When both values are "Unknown", they are considered equal
 *
 * @template TData - The type of data in the table row
 * @param rowA - First table row to compare
 * @param rowB - Second table row to compare
 * @param columnId - The ID of the column containing the date value to sort by
 * @returns -1 if rowA should come before rowB, 1 if rowA should come after rowB, 0 if equal
 *
 * @example
 * // This function always sorts the same way:
 * // "Unknown", "Unknown", "2023-01-01", "2023-06-15", "2024-01-01"
 * //
 * // TanStack Table handles ascending/descending by inverting the comparison results.
 * // When descending, TanStack automatically reverses this to:
 * // "Unknown", "Unknown", "2024-01-01", "2023-06-15", "2023-01-01"
 *
 * @example
 * // Usage in TanStack Table column definition:
 * {
 *   accessorKey: 'paidDate',
 *   header: 'Date Paid',
 *   sortingFn: sortByDate,
 * }
 */
export const sortByDate = <TData>(rowA: Row<TData>, rowB: Row<TData>, columnId: string) => {
  const paidDateA = rowA.getValue<string>(columnId);
  const paidDateB = rowB.getValue<string>(columnId);

  if (paidDateA === 'Unknown' && paidDateB !== 'Unknown') {
    return -1;
  }

  if (paidDateB === 'Unknown' && paidDateA !== 'Unknown') {
    return 1;
  }

  if (paidDateA === 'Unknown' && paidDateB === 'Unknown') {
    return 0;
  }

  if (isBefore(new UTCDate(paidDateA), new UTCDate(paidDateB))) {
    return -1;
  }
  if (isAfter(new UTCDate(paidDateA), new UTCDate(paidDateB))) {
    return 1;
  }

  return 0;
};
